import React, { useState, useEffect } from 'react';
import { CubeIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';

const General = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    module_inventory_items_enabled: true,
    module_inventory_services_enabled: true,
    module_reservations_items_enabled: true,
    module_reservations_services_enabled: true,
  });

  // Fetch current settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const moduleResponse = await axios.get('/api/settings/module-visibility', {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (moduleResponse.data) {
          setSettings(moduleResponse.data);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [token]);

  // Handle toggle change
  const handleToggle = async (key) => {
    const newSettings = {
      ...settings,
      [key]: !settings[key],
    };
    setSettings(newSettings);

    // Save to backend
    setSaving(true);
    try {
      await axios.put(
        '/api/settings/module-visibility',
        newSettings,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
      // Revert on error
      setSettings(settings);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            General Settings
          </h2>
          <p className="mt-1 text-gray-600">
            Configure general store settings and module visibility
          </p>
        </div>
      </div>

      {/* Module Visibility Settings */}
      <div className="mt-8 space-y-6">
        {/* Inventory Modules */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <CubeIcon className="h-6 w-6 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Inventory Modules</h3>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            Control which inventory tabs are visible in the Inventory section
          </p>

          <div className="space-y-4">
            {/* Items & Product Types Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Items & Product Types
                </label>
                <p className="text-sm text-gray-500">
                  Show inventory items and product types management tabs
                </p>
              </div>
              <button
                type="button"
                className={`${
                  settings.module_inventory_items_enabled
                    ? 'bg-blue-600'
                    : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  saving ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={() => handleToggle('module_inventory_items_enabled')}
                disabled={saving}
              >
                <span
                  className={`${
                    settings.module_inventory_items_enabled ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>

            {/* Inventory Services Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Services
                </label>
                <p className="text-sm text-gray-500">
                  Show services management tab in Inventory section
                </p>
              </div>
              <button
                type="button"
                className={`${
                  settings.module_inventory_services_enabled
                    ? 'bg-blue-600'
                    : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  saving ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={() => handleToggle('module_inventory_services_enabled')}
                disabled={saving}
              >
                <span
                  className={`${
                    settings.module_inventory_services_enabled ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Reservations Modules */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <CalendarIcon className="h-6 w-6 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Reservations Management Modules</h3>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            Control which tabs are visible in the Reservations Management section
          </p>

          <div className="space-y-4">
            {/* Reservations Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Reservations
                </label>
                <p className="text-sm text-gray-500">
                  Show item reservations tab
                </p>
              </div>
              <button
                type="button"
                className={`${
                  settings.module_reservations_items_enabled
                    ? 'bg-blue-600'
                    : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  saving ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={() => handleToggle('module_reservations_items_enabled')}
                disabled={saving}
              >
                <span
                  className={`${
                    settings.module_reservations_items_enabled ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>

            {/* Reservation Services Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Services
                </label>
                <p className="text-sm text-gray-500">
                  Show service reservations tab
                </p>
              </div>
              <button
                type="button"
                className={`${
                  settings.module_reservations_services_enabled
                    ? 'bg-blue-600'
                    : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  saving ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={() => handleToggle('module_reservations_services_enabled')}
                disabled={saving}
              >
                <span
                  className={`${
                    settings.module_reservations_services_enabled ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default General;
